<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { AdjustmentsHorizontalIcon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'
import Cookies from 'js-cookie'

export interface ColumnConfig {
  field: string
  headerName: string
  visible?: boolean
  locked?: boolean // Pour les colonnes qui ne peuvent pas être cachées (ex: actions)
}

const props = defineProps<{
  columns: ColumnConfig[]
  cookieName: string // Nom du cookie pour sauvegarder les préférences
}>()

const emit = defineEmits<{
  columnsChanged: [visibleColumns: string[]]
}>()

// État local des colonnes visibles
const columnVisibility = ref<Record<string, boolean>>({})

// Initialiser depuis les cookies ou les valeurs par défaut
function initializeColumnVisibility() {
  const savedPreferences = Cookies.get(props.cookieName)

  if (savedPreferences) {
    try {
      const parsed = JSON.parse(savedPreferences)
      const merged = props.columns.reduce(
        (acc, col) => {
          acc[col.field] = col.field in parsed ? parsed[col.field] : col.visible !== false
          return acc
        },
        {} as Record<string, boolean>,
      )
      columnVisibility.value = merged
    } catch (e) {
      console.warn('Failed to parse column preferences from cookie', e)
      setDefaultVisibility()
    }
  } else {
    setDefaultVisibility()
  }
}

function setDefaultVisibility() {
  columnVisibility.value = props.columns.reduce(
    (acc, col) => {
      acc[col.field] = col.visible !== false
      return acc
    },
    {} as Record<string, boolean>,
  )
}

// Calculer les colonnes visibles
const visibleColumnFields = computed(() => {
  return Object.entries(columnVisibility.value)
    .filter(([_, visible]) => visible)
    .map(([field]) => field)
})

// Sauvegarder dans les cookies quand ça change
watch(
  columnVisibility,
  (newValue) => {
    Cookies.set(props.cookieName, JSON.stringify(newValue), { expires: 365 })
    emit('columnsChanged', visibleColumnFields.value)
  },
  { deep: true },
)

// Toggle une colonne
function toggleColumn(field: string) {
  const column = props.columns.find((c) => c.field === field)
  if (column?.locked) return

  columnVisibility.value[field] = !columnVisibility.value[field]
}

// Tout afficher
function showAll() {
  props.columns.forEach((col) => {
    columnVisibility.value[col.field] = true
  })
}

// Tout cacher (sauf les colonnes lockées)
function hideAll() {
  props.columns.forEach((col) => {
    if (!col.locked) {
      columnVisibility.value[col.field] = false
    }
  })
}

// Réinitialiser aux valeurs par défaut
function resetToDefault() {
  setDefaultVisibility()
}

// Initialiser au montage
initializeColumnVisibility()

// Émettre les colonnes visibles au départ
emit('columnsChanged', visibleColumnFields.value)
</script>

<template>
  <div class="dropdown dropdown-end">
    <div tabindex="0" class="btn btn-ghost btn-sm gap-2" role="button">
      <AdjustmentsHorizontalIcon class="w-5 h-5" />
      <span class="hidden sm:inline">Colonnes</span>
    </div>
    <div tabindex="-1" class="dropdown-content z-[100] menu p-4 shadow-lg bg-base-100 rounded-box w-72 border border-base-300">
      <div class="flex justify-between items-center mb-3">
        <h3 class="font-semibold text-sm">Afficher les colonnes</h3>
      </div>

      <div class="divider my-1"></div>

      <div class="max-h-64 overflow-y-auto space-y-1">
        <label
          v-for="column in columns"
          :key="column.field"
          class="flex items-center gap-2 p-2 rounded hover:bg-base-200 cursor-pointer transition-colors"
          :class="{ hidden: column.locked }"
        >
          <input
            type="checkbox"
            class="checkbox checkbox-sm"
            :checked="columnVisibility[column.field]"
            :disabled="column.locked"
            @change="toggleColumn(column.field)"
          />
          <span class="text-sm flex-1">{{ column.headerName }}</span>
          <component :is="columnVisibility[column.field] ? EyeIcon : EyeSlashIcon" class="w-4 h-4 opacity-50" />
        </label>
      </div>

      <div class="divider my-2"></div>

      <div class="flex gap-2 text-xs">
        <button class="btn btn-ghost btn-xs flex-1" @click="showAll" title="Tout afficher">Tout afficher</button>
        <button class="btn btn-ghost btn-xs flex-1" @click="hideAll" title="Tout cacher">Tout cacher</button>
        <button class="btn btn-ghost btn-xs flex-1" @click="resetToDefault" title="Réinitialiser">Réinitialiser</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dropdown-content {
  margin-top: 8px;
}
</style>
